import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Application, Currency, UserSettings, ApplicationStatus, RejectReason, RejectReasonCode } from '../types'
import { statusBadgeClass, rejectOptions } from '../utils'
import { STATUS_LABELS } from '../utils/statusMachine'
import { ChatBubbleLeftIcon, ClockIcon, StarIcon as StarOutline, LinkIcon, ExclamationCircleIcon, ArchiveBoxIcon, NoSymbolIcon, XMarkIcon, CheckCircleIcon, ArrowUturnLeftIcon, BellIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarSolid } from '@heroicons/react/24/solid'
import {
  convertSalaryRange,
  convertCurrency,
  convertToDisplayFormat,
  convertPeriod,
  getFormatSuffix,
  getPeriodSuffix,
  formatCurrencyAmount,
} from '../utils/currency'

const formatRelative = (date: Date) => {
  const ts = date.getTime()
  if (!Number.isFinite(ts)) return '—'

  const diffMs = ts - Date.now()
  const diffMinutes = Math.round(diffMs / (1000 * 60))

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

  const absMinutes = Math.abs(diffMinutes)
  if (absMinutes < 60) return rtf.format(diffMinutes, 'minute')

  const diffHours = Math.round(diffMinutes / 60)
  const absHours = Math.abs(diffHours)
  if (absHours < 24) return rtf.format(diffHours, 'hour')

  const diffDays = Math.round(diffHours / 24)
  const absDays = Math.abs(diffDays)
  if (absDays < 30) return rtf.format(diffDays, 'day')

  const diffMonths = Math.round(diffDays / 30)
  const absMonths = Math.abs(diffMonths)
  if (absMonths < 12) return rtf.format(diffMonths, 'month')

  const diffYears = Math.round(diffMonths / 12)
  return rtf.format(diffYears, 'year')
}

const extractDomain = (url: string): string => {
  try {
    const parsed = new URL(url)
    return parsed.hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

type Props = {
  app: Application
  settings: UserSettings
  onToggleFavorite: (appId: string) => void
  onOpenNoteModal: (appId: string) => void
  onUpdateStatus: (appId: string, newStatus: ApplicationStatus) => void
  onSubmitReject: (appId: string, reason: RejectReason) => void
  onArchive: (appId: string) => void
  onUnarchive: (appId: string) => void
  onToggleStage: (appId: string, stageId: string) => void
  onSetFollowUp?: (appId: string) => void
  bulkEditMode?: boolean
  isSelected?: boolean
  onToggleSelect?: (appId: string) => void
}

export const CompactApplicationCard = ({ app, settings, onToggleFavorite, onOpenNoteModal, onSubmitReject, onArchive, onUnarchive, onToggleStage, onSetFollowUp, bulkEditMode, isSelected, onToggleSelect }: Props) => {
  const navigate = useNavigate()
  const [showNoteTooltip, setShowNoteTooltip] = useState(false)
  const [showRejectTooltip, setShowRejectTooltip] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [rejectState, setRejectState] = useState<RejectReason>({ code: 'too_expensive' })
  const updatedAt = useMemo(
    () => new Date(app.updatedAt || app.createdAt),
    [app.updatedAt, app.createdAt]
  )

  // Calculate stages progress
  const completedStages = app.processStages.filter((s) => s.completed).length
  const totalStages = app.processStages.length
  const progressPercent = totalStages > 0 ? Math.round((completedStages / totalStages) * 100) : 0

  // Get display settings
  const { currencyConversionEnabled, targetCurrency, salaryDisplayFormat } = settings

  // Format salary range display
  const salaryRangeDisplay = useMemo(() => {
    if (!app.salaryRange) return null

    // Apply currency conversion if enabled
    const displayRange = currencyConversionEnabled
      ? convertSalaryRange(app.salaryRange, targetCurrency)
      : app.salaryRange

    if (!displayRange) return null

    const currency = (displayRange.currency as Currency) || 'USD'
    // Use original period from salary range, or fallback to display format
    const periodSuffix = salaryDisplayFormat === 'original'
      ? getPeriodSuffix(displayRange.period)
      : getFormatSuffix(salaryDisplayFormat)

    // First convert to monthly base, then to target format
    const min = salaryDisplayFormat === 'original'
      ? displayRange.min
      : convertToDisplayFormat(
          convertPeriod(displayRange.min, displayRange.period, 'monthly'),
          salaryDisplayFormat
        )
    const max = salaryDisplayFormat === 'original'
      ? displayRange.max
      : convertToDisplayFormat(
          convertPeriod(displayRange.max, displayRange.period, 'monthly'),
          salaryDisplayFormat
        )

    if (min == null || max == null) return null

    const minFormatted = formatCurrencyAmount(min, currency)
    const maxFormatted = formatCurrencyAmount(max, currency)

    return `${minFormatted} – ${maxFormatted}${periodSuffix}`
  }, [app.salaryRange, currencyConversionEnabled, targetCurrency, salaryDisplayFormat])

  // Format target display using new target fields
  const targetDisplay = useMemo(() => {
    if (app.targetAmount == null) return null

    // Get original currency from app, default to USD if 'original' or missing
    const appCurrency = (app.targetCurrency && app.targetCurrency !== 'original') ? app.targetCurrency : 'USD'
    const appPeriod = app.targetPeriod || 'monthly'
    
    let displayAmount = app.targetAmount
    let displayCurrency = appCurrency
    
    // Apply currency conversion if enabled and targetCurrency is not 'original'
    if (settings.currencyConversionEnabled && settings.targetCurrency !== 'original') {
      // If currencies are the same, no conversion needed
      if (appCurrency === settings.targetCurrency) {
        displayCurrency = settings.targetCurrency
      } else {
        // Convert to target currency
        const converted = convertCurrency(app.targetAmount, appCurrency, settings.targetCurrency as Exclude<Currency, 'original'>)
        if (converted != null) {
          displayAmount = converted
          displayCurrency = settings.targetCurrency
        }
      }
    }
    // If targetCurrency is 'original', keep original currency (already set above)
    
    // Convert period if format changed
    const suffix = settings.salaryDisplayFormat === 'original'
      ? getPeriodSuffix(appPeriod)
      : getFormatSuffix(settings.salaryDisplayFormat)
    
    let finalAmount = displayAmount
    if (settings.salaryDisplayFormat !== 'original') {
      // First convert to monthly base, then to target format
      const monthlyAmount = convertPeriod(displayAmount, appPeriod, 'monthly')
      finalAmount = convertToDisplayFormat(monthlyAmount, settings.salaryDisplayFormat) || displayAmount
    }
    
    const formatted = formatCurrencyAmount(finalAmount, displayCurrency)
    return `${formatted}${suffix}`
  }, [app.targetAmount, app.targetPeriod, app.targetCurrency, settings.salaryDisplayFormat, settings.targetCurrency, settings.currencyConversionEnabled])

  // SLA warning
  const sla = useMemo(() => {
    if (app.status !== 'applied' && app.status !== 'in_process') return null
    const base = new Date(app.updatedAt || app.createdAt).getTime()
    const days = Math.floor((Date.now() - base) / (1000 * 60 * 60 * 24))
    if (!Number.isFinite(days)) return null
    if (days >= 14) return { level: 'critical' as const, days }
    if (days >= 7) return { level: 'warn' as const, days }
    return null
  }, [app.status, app.updatedAt, app.createdAt])

  // Find next incomplete stage
  const nextStage = useMemo(() => {
    return app.processStages.find((stage) => !stage.completed)
  }, [app.processStages])

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleFavorite(app.id)
  }

  const handleCardClick = () => {
    if (bulkEditMode && onToggleSelect) {
      onToggleSelect(app.id)
    } else {
      navigate(`/app/${app.id}`)
    }
  }

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleSelect?.(app.id)
  }

  return (
    <div
      onClick={handleCardClick}
      className={`card w-full h-full p-4 text-left transition-all hover:shadow-app-hover hover:border-app-line hover:bg-app-subtle/80 focus:outline-none focus:ring-2 focus:ring-app-accent/50 group flex flex-col cursor-pointer ${
        isSelected ? 'ring-2 ring-app-accent/50 border-app-accent' : ''
      }`}
    >
      {/* Header: Company + Favorite + Status */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          {/* Checkbox in bulk edit mode */}
          {bulkEditMode ? (
            <div onClick={handleCheckboxClick} className="shrink-0 pt-0.5">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}}
                className="h-4 w-4 border-app-line bg-app-subtle text-app-muted focus:ring-app-line focus:ring-offset-white dark:focus:ring-offset-app-line cursor-pointer rounded-lg"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={handleFavoriteClick}
              className={`shrink-0 p-0.5 rounded transition-colors ${
                app.favorite
                  ? 'text-amber-700 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300'
                  : 'text-app-muted hover:text-amber-700'
              }`}
              title={app.favorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              {app.favorite ? (
                <StarSolid className="h-4 w-4" />
              ) : (
                <StarOutline className="h-4 w-4" />
              )}
            </button>
          )}
          <h3 className="text-base font-semibold text-app-ink leading-tight group-hover:text-app-accent transition-colors line-clamp-2 font-display">
            {app.company}
          </h3>
        </div>
        <span className={`badge text-xs shrink-0 ${statusBadgeClass[app.status]}`}>
          {STATUS_LABELS[app.status]}
        </span>
      </div>

      {/* Salary Info Section */}
      <div className="mb-3 space-y-1">
        {/* Salary Range (from posting) */}
        {salaryRangeDisplay ? (
          <div>
            <span className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{salaryRangeDisplay}</span>
          </div>
        ) : (
          <div>
            <span className="text-sm text-app-muted">No salary range</span>
          </div>
        )}
        
        {/* Target */}
        {targetDisplay && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase text-app-muted">Target:</span>
            <span className="text-sm text-sky-700 dark:text-sky-300">{targetDisplay}</span>
          </div>
        )}
      </div>

      {/* Tech stack (first 3) */}
      {app.techStack.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          {app.techStack.slice(0, 3).map((tech) => (
            <span
              key={tech}
              className="rounded bg-app-subtle/80 px-1.5 py-0.5 text-[10px] text-app-muted"
            >
              {tech}
            </span>
          ))}
          {app.techStack.length > 3 && (
            <span className="text-[10px] text-app-muted">+{app.techStack.length - 3}</span>
          )}
        </div>
      )}

      {/* Spacer to push footer to bottom */}
      <div className="flex-1" />

      {/* Footer: Progress + Meta */}
      <div className="pt-3 border-t border-app-line/50 space-y-2">
        {/* Stages progress bar */}
        {totalStages > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-[10px] text-app-muted">
              <span>Progress</span>
              <span>{completedStages}/{totalStages}</span>
            </div>
            <div className="h-1.5 bg-app-subtle rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  progressPercent === 100
                    ? 'bg-emerald-500'
                    : progressPercent > 50
                      ? 'bg-sky-500'
                      : 'bg-sky-700'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Meta row: date, contract type, note, SLA */}
        <div className="flex items-center justify-between text-[11px] text-app-muted">
          <div className="flex items-center gap-1">
            <ClockIcon className="h-3 w-3" />
            {formatRelative(updatedAt)}
          </div>
          <div className="flex items-center gap-2">
            {app.contractType && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-app-line/60 text-app-muted">
                {app.contractType}
              </span>
            )}
            {app.archived && (
              <span className="text-[10px] text-app-muted">archived</span>
            )}
            {sla && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                  sla.level === 'critical'
                    ? 'bg-rose-50 dark:bg-rose-900/70 text-rose-700 dark:text-rose-200'
                    : 'bg-amber-50 dark:bg-amber-900/70 text-amber-700 dark:text-amber-200'
                }`}
              >
                {sla.days}d
              </span>
            )}
            {/* Link to job posting */}
            {app.sourceUrl && (
              <a
                href={app.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-0.5 transition-colors text-app-accent/70 hover:text-app-accent-hover rounded-lg"
                title={extractDomain(app.sourceUrl)}
              >
                <LinkIcon className="h-3 w-3" />
              </a>
            )}
            {/* Reject reason with tooltip */}
            {app.rejectReason && (
              <div className="relative">
                <button
                  type="button"
                  className="p-0.5 transition-colors text-rose-700 dark:text-rose-400/70 hover:text-rose-700 dark:hover:text-rose-300 bg-rose-50 dark:bg-rose-900/30 rounded-lg"
                  title="Reject reason"
                  onMouseEnter={() => setShowRejectTooltip(true)}
                  onMouseLeave={() => setShowRejectTooltip(false)}
                >
                  <ExclamationCircleIcon className="h-3 w-3" />
                </button>
                {showRejectTooltip && (
                  <div className="absolute bottom-full right-0 mb-1 bg-app-subtle text-app-ink text-xs rounded px-2 py-1 whitespace-normal max-w-xs z-50 border border-app-line shadow-lg">
                    <div className="font-semibold text-rose-500 dark:text-rose-300 mb-0.5">{app.rejectReason.code.replace(/_/g, ' ')}</div>
                    {app.rejectReason.details && <div>{app.rejectReason.details}</div>}
                    <div className="absolute top-full right-2 w-0 h-0 border-4 border-transparent border-t-app-line" />
                  </div>
                )}
              </div>
            )}
            {/* Note icon button with tooltip */}
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenNoteModal(app.id)
                }}
                onMouseEnter={() => app.note && setShowNoteTooltip(true)}
                onMouseLeave={() => setShowNoteTooltip(false)}
                className={`p-0.5 rounded transition-colors ${
                  app.note
                    ? 'text-app-accent hover:text-app-accent-hover bg-app-highlight/40'
                    : 'text-app-muted hover:text-app-muted'
                }`}
                title={app.note ? 'Edit note' : 'Add note'}
              >
                <ChatBubbleLeftIcon className="h-3 w-3" />
              </button>
              {/* Tooltip */}
              {showNoteTooltip && app.note && (
                <div className="absolute bottom-full right-0 mb-1 bg-app-subtle text-app-ink text-xs rounded px-2 py-1 whitespace-normal max-w-xs z-50 border border-app-line shadow-lg">
                  {app.note}
                  <div className="absolute top-full right-2 w-0 h-0 border-4 border-transparent border-t-app-line" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick actions: Next Stage, Reject, Archive and Restore */}
        <div className="flex flex-wrap gap-1 pt-2 border-t border-app-line/30">
          {/* Complete next stage button - hidden for offer, ghosted, accepted */}
          {nextStage && !app.archived && app.status !== 'rejected' && app.status !== 'offer' && app.status !== 'ghosted' && app.status !== 'accepted' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onToggleStage(app.id, nextStage.id)
              }}
              className="text-[10px] px-2 py-1 bg-sky-200 dark:bg-sky-900/40 text-sky-900 dark:text-sky-300 hover:bg-sky-300 dark:hover:bg-sky-900/60 transition-colors flex items-center gap-1 font-medium max-w-[140px] rounded-lg"
              title={`Mark "${nextStage.name}" as completed`}
            >
              <CheckCircleIcon className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{nextStage.name}</span>
            </button>
          )}
          {app.status !== 'rejected' && !app.archived && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowRejectModal(true)
                  setRejectState({ code: 'too_expensive' })
                }}
                className="text-[10px] px-2 py-1 border border-rose-300/50 dark:border-rose-900/50 bg-rose-200 dark:bg-rose-900/40 text-rose-900 dark:text-rose-300 hover:bg-rose-300 dark:hover:bg-rose-900/60 transition-colors flex items-center gap-1 font-medium rounded-lg"
                title="Reject application"
              >
                <NoSymbolIcon className="h-3 w-3" />
                Reject
              </button>
            )}
            {!app.archived && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowArchiveConfirm(true)
                }}
                className="text-[10px] px-2 py-1 bg-app-line/50 text-app-muted hover:bg-app-surface hover:text-app-ink transition-colors flex items-center gap-1 rounded-lg"
                title="Archive application"
              >
                <ArchiveBoxIcon className="h-3 w-3" />
                Archive
              </button>
            )}
            {!app.archived && onSetFollowUp && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onSetFollowUp(app.id)
                }}
                className="text-[10px] px-2 py-1 bg-amber-200/50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-300 dark:hover:bg-amber-900/50 transition-colors flex items-center gap-1 rounded-lg"
                title={app.followUpAt ? 'Edit reminder' : 'Set reminder'}
              >
                <BellIcon className="h-3 w-3" />
                {app.followUpAt ? 'Edit' : 'Remind'}
              </button>
            )}
            {/* Restore button for archived items */}
            {app.archived && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onUnarchive(app.id)
                }}
                className="text-[10px] px-2 py-1 bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/60 transition-colors flex items-center gap-1 rounded-lg"
                title="Restore to active"
              >
                <ArrowUturnLeftIcon className="h-3 w-3" />
                Restore
              </button>
            )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && !app.archived && app.status !== 'rejected' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-app-ink/35 dark:bg-black/60 px-4" onClick={(e) => {
          e.stopPropagation()
          if (e.target === e.currentTarget) {
            setShowRejectModal(false)
          }
        }}>
          <div className="w-full max-w-md border border-app-line bg-app-surface p-4 space-y-4 rounded-2xl shadow-app-card" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-app-ink flex items-center gap-2">
                <NoSymbolIcon className="h-4 w-4 text-rose-700 dark:text-rose-400" aria-hidden="true" />
                Reject: {app.company}
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowRejectModal(false)
                }}
                className="text-app-muted hover:text-app-ink"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs uppercase text-app-muted font-medium">Reason</label>
                <select
                  value={rejectState.code}
                  onChange={(e) =>
                    setRejectState({ ...rejectState, code: e.target.value as RejectReasonCode })
                  }
                  className="w-full rounded-lg border border-app-line bg-app-page px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                >
                  {rejectOptions.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs uppercase text-app-muted font-medium">Details (optional)</label>
                <input
                  type="text"
                  placeholder="Additional context..."
                  value={rejectState.details ?? ''}
                  onChange={(e) => setRejectState({ ...rejectState, details: e.target.value })}
                  className="w-full rounded-lg border border-app-line bg-app-page px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowRejectModal(false)
                }}
                className="rounded-lg border border-app-line px-4 py-2 text-sm font-semibold text-app-ink hover:bg-app-subtle"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onSubmitReject(app.id, rejectState)
                  setShowRejectModal(false)
                }}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-app-ink/35 dark:bg-black/60 px-4" onClick={(e) => {
          e.stopPropagation()
          if (e.target === e.currentTarget) {
            setShowArchiveConfirm(false)
          }
        }}>
          <div className="w-full max-w-sm border border-app-line bg-app-surface p-4 space-y-4 rounded-2xl shadow-app-card" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-app-ink flex items-center gap-2">
                <ArchiveBoxIcon className="h-4 w-4 text-app-muted" aria-hidden="true" />
                Archive application
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowArchiveConfirm(false)
                }}
                className="text-app-muted hover:text-app-ink"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-app-muted">
              Are you sure you want to archive <span className="font-semibold text-app-ink">{app.company}</span>?
            </p>
            <p className="text-xs text-app-muted">
              You can restore it later from the archive view.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowArchiveConfirm(false)
                }}
                className="rounded-lg border border-app-line px-4 py-2 text-sm font-semibold text-app-ink hover:bg-app-subtle"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onArchive(app.id)
                  setShowArchiveConfirm(false)
                }}
                className="rounded-lg bg-app-line px-4 py-2 text-sm font-semibold text-app-ink hover:bg-app-surface"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
